"use client";

import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Textarea } from "@/app/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Badge } from "@/app/components/ui/badge";
import { Slider } from "@/app/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import {
  MessageCircle,
  Send,
  RotateCcw,
  Download,
  Copy,
  Bot,
  User,
  Loader2,
  AlertTriangle,
  Clock,
  Settings,
  Zap,
  Brain,
  Star,
  DollarSign,
  Info,
  Sparkles,
  Database
} from "lucide-react";
import { AI_MODELS, getModelById, getModelsByCategory, type AIModel } from "@/lib/ai-models";
import { ProviderIcon, getProviderColor } from "@/lib/provider-icons";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/hooks/useAuth";
import Link from "next/link";

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  model?: string;
  tokensUsed?: number;
  cost?: number;
}

export default function AIChatPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [temperature, setTemperature] = useState([0.7]);
  const [maxTokens, setMaxTokens] = useState([1000]);
  const [currentTime, setCurrentTime] = useState<Date | null>(null);
  const [totalCost, setTotalCost] = useState(0);
  const [totalTokens, setTotalTokens] = useState(0);
  const [activeTab, setActiveTab] = useState("chat");
  const [mounted, setMounted] = useState(false);
  const [showModelsDropdown, setShowModelsDropdown] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { user, loading: authLoading } = useAuth();

  // TRPC queries and mutations - only run when user is authenticated
  const { data: dailyUsageData, refetch: refetchUsage } = trpc.usage.getFeatureUsage.useQuery(
    { featureId: 'ai-chat', period: 'daily' },
    { enabled: !!user }
  );
  const { data: monthlyUsageData } = trpc.usage.getFeatureUsage.useQuery(
    { featureId: 'ai-chat', period: 'monthly' },
    { enabled: !!user }
  );
  const { data: userAllowedModels, refetch: refetchAllowedModels } = trpc.aiChat.getUserAllowedModels.useQuery(
    undefined,
    { enabled: !!user }
  );
  const sendChatMessage = trpc.aiChat.send.useMutation();

  // Transform usage data to match expected structure
  const usageData = dailyUsageData && monthlyUsageData ? {
    tier: dailyUsageData.tier,
    usage: {
      daily: dailyUsageData.usage,
      monthly: monthlyUsageData.usage
    },
    limits: dailyUsageData.limits
  } : null;

  // Check for usage tracking errors
  const hasUsageError = !dailyUsageData && !monthlyUsageData;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Set mounted state to prevent hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Set default model based on user's plan when data loads
  useEffect(() => {
    if (userAllowedModels && !selectedModel) {
      // Default to first available model in fast category, or first available model overall
      const fastModels = getModelsByCategory('fast').filter(model =>
        userAllowedModels.allowedModels.includes('*') ||
        userAllowedModels.allowedModels.includes(model.id)
      );

      const firstAvailableModel = fastModels.length > 0 ? fastModels[0].id :
        userAllowedModels.allowedModels.find(modelId => modelId !== '*') || 'gemini-2.0-flash';

      setSelectedModel(firstAvailableModel);
    }
  }, [userAllowedModels, selectedModel]);

  // Update time every second
  useEffect(() => {
    // Set initial time after hydration
    setCurrentTime(new Date());
    
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isProcessing || !selectedModel) return;

    // Check if selected model is available in user's plan
    if (userAllowedModels && !userAllowedModels.allowedModels.includes('*') && !userAllowedModels.allowedModels.includes(selectedModel)) {
      setError(`Model "${selectedModel}" is not available in your ${userAllowedModels.tier} plan. Please select a different model or upgrade your plan.`);
      toast.error('Model not available in your plan');
      return;
    }

    const userMessage: Message = {
      id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      content: inputMessage,
      role: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsProcessing(true);
    setError("");

    try {
      const result = await sendChatMessage.mutateAsync({
        message: inputMessage,
        model: selectedModel,
        conversationHistory: messages.map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        temperature: temperature[0],
        maxTokens: maxTokens[0]
      });
      
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        content: result.response || '',
        role: 'assistant',
        timestamp: new Date(),
        model: result.model || selectedModel,
        tokensUsed: result.tokensUsed || 0,
        cost: result.cost || 0
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Update totals
      const cost = result.cost || 0;
      const tokens = result.tokensUsed || 0;
      
      if (cost > 0) {
        setTotalCost(prev => prev + cost);
      }
      if (tokens > 0) {
        setTotalTokens(prev => prev + tokens);
      }

      // Refresh usage data
      await refetchUsage();

      toast.success('Response received!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to get response";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError("");
    setTotalCost(0);
    setTotalTokens(0);
    toast.success('Chat cleared!');
  };

  const copyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
    toast.success('Message copied!');
  };

  const downloadChat = () => {
    const chatText = messages.map(msg => 
      `${msg.role === 'user' ? 'You' : 'AI'} (${msg.model || 'Unknown Model'}): ${msg.content}${msg.tokensUsed ? `\nTokens: ${msg.tokensUsed}` : ''}${msg.cost ? `\nCost: $${msg.cost.toFixed(6)}` : ''}`
    ).join('\n\n');
    
    const blob = new Blob([chatText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ai-chat-${currentTime ? currentTime.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Chat downloaded!');
  };

  const getCurrentModel = () => selectedModel ? getModelById(selectedModel) : null;

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'fast': return <Zap className="h-4 w-4" />;
      case 'balanced': return <Brain className="h-4 w-4" />;
      case 'powerful': return <Star className="h-4 w-4" />;
      case 'specialized': return <Sparkles className="h-4 w-4" />;
      default: return <Bot className="h-4 w-4" />;
    }
  };

  const getModelIcon = (model: AIModel) => {
    return (
      <ProviderIcon 
        provider={model.provider} 
        className={`h-4 w-4 ${getProviderColor(model.provider)}`}
      />
    );
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'fast': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'balanced': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'powerful': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'specialized': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show sign-in prompt when not authenticated
  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8 animate-fade-in">
        <div className="max-w-md mx-auto">
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                Sign in to use AI Chat
              </CardTitle>
              <CardDescription>
                AI Chat requires an account to track usage and manage your subscription. Sign in or create an account to get started.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Button asChild>
                <Link href="/auth/login">Sign in</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link href="/auth/signup">Create account</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 animate-slide-in-left">AI Chat Assistant</h1>
          <p className="text-muted-foreground text-lg animate-slide-in-right">
            Chat with multiple AI models powered by Eden AI, OpenAI, and Google
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-2">
            <Card className="card-enhanced h-fit max-h-[calc(100vh-8rem)]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  <span>Settings</span>
                </CardTitle>
                <CardDescription className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{mounted && currentTime ? currentTime.toLocaleTimeString() : '--:--:--'}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 overflow-hidden">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
                  <TabsList className="grid w-full grid-cols-1 shrink-0">
                    <TabsTrigger value="chat">Chat</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="chat" className="space-y-4 flex-1 overflow-y-auto min-h-0">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Update Model</label>
                      <Button
                        onClick={() => setShowModelsDropdown(!showModelsDropdown)}
                        variant="outline"
                        className="w-full hover-lift"
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        {showModelsDropdown ? 'Hide Models' : 'Browse Models'}
                      </Button>
                    </div>

                    {/* Usage Setup Warning */}
                    {hasUsageError && (
                      <Alert className="border-orange-200 bg-orange-50">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        <AlertDescription className="text-orange-800">
                          Usage tracking unavailable. The required Appwrite collections may not be set up.{' '}
                          <a href="/admin/usage-setup" className="font-medium underline">
                            Set up usage tracking
                          </a>
                        </AlertDescription>
                      </Alert>
                    )}



                    {showModelsDropdown && (
                      <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium">Available Models</h3>
                          <Button
                            onClick={() => setShowModelsDropdown(false)}
                            variant="ghost"
                            size="sm"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                          {(['fast', 'balanced', 'powerful', 'specialized'] as const).map((category) => {
                            const categoryModels = getModelsByCategory(category).filter(model =>
                              userAllowedModels?.allowedModels.includes('*') ||
                              userAllowedModels?.allowedModels.includes(model.id)
                            );

                            if (categoryModels.length === 0) {
                              const allCategoryModels = getModelsByCategory(category);
                              const hasRestrictedModels = allCategoryModels.some(model =>
                                !userAllowedModels?.allowedModels.includes('*') &&
                                !userAllowedModels?.allowedModels.includes(model.id)
                              );

                              if (hasRestrictedModels) {
                                return (
                                  <div key={category} className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      {getCategoryIcon(category)}
                                      <h4 className="font-medium capitalize text-muted-foreground">{category} Models</h4>
                                      <Badge variant="outline" className="text-xs text-orange-600">
                                        Upgrade Required
                                      </Badge>
                                    </div>
                                    <div className="p-3 rounded-lg border border-orange-200 bg-orange-50 dark:bg-orange-950/30">
                                      <p className="text-sm text-orange-800 dark:text-orange-200 mb-2">
                                        {category === 'powerful' || category === 'specialized'
                                          ? `Premium ${category} models require Pro plan or higher.`
                                          : `${category} models limited in Free plan.`
                                        }
                                      </p>
                                      <Button size="sm" variant="outline" className="text-xs">
                                        <a href="/pricing">Upgrade to Access</a>
                                      </Button>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }

                            return (
                              <div key={category} className="space-y-2">
                                <div className="flex items-center gap-2">
                                  {getCategoryIcon(category)}
                                  <h4 className="font-medium capitalize">{category} Models</h4>
                                  <Badge variant="outline" className="text-xs">
                                    {categoryModels.length} available
                                  </Badge>
                                </div>
                                <div className="space-y-2">
                                  {categoryModels.map((model) => (
                                    <div
                                      key={model.id}
                                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                        selectedModel === model.id
                                          ? 'border-primary bg-primary/5'
                                          : 'border-border hover:border-primary/50'
                                      }`}
                                      onClick={() => {
                                        setSelectedModel(model.id);
                                        setShowModelsDropdown(false);
                                      }}
                                    >
                                      <div className="flex items-center justify-between mb-2 gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                          {getModelIcon(model)}
                                          <h5 className="font-medium truncate">{model.name}</h5>
                                        </div>
                                        <Badge variant="outline" className="text-xs shrink-0">
                                          {model.provider}
                                        </Badge>
                                      </div>
                                      <p className="text-sm text-muted-foreground mb-2 wrap-break-word">
                                        {model.description}
                                      </p>
                                      <div className="flex items-center justify-between gap-2">
                                        <Badge variant="secondary" className="text-xs shrink-0">
                                          {model.pricing}
                                        </Badge>
                                        <div className="flex gap-1 flex-wrap">
                                          {model.features.slice(0, 2).map((feature, index) => (
                                            <Badge key={index} variant="outline" className="text-xs">
                                              {feature}
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Usage Information */}
                    {usageData && (
                      <div className="p-3 bg-muted/50 rounded-lg space-y-3">
                        <div className="flex items-center gap-2">
                          <Zap className="h-4 w-4 text-blue-500" />
                          <span className="text-sm font-medium">Usage ({usageData.tier})</span>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-xs">
                            <span>Daily Requests</span>
                            <span className="font-mono">
                              {usageData.usage.daily.requests}
                              {usageData.limits?.daily?.requests && usageData.limits.daily.requests !== -1 
                                ? `/${usageData.limits.daily.requests}` 
                                : '/∞'
                              }
                            </span>
                          </div>
                          {usageData.limits?.daily?.requests && usageData.limits.daily.requests !== -1 && (
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div 
                                className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                style={{ 
                                  width: `${Math.min((usageData.usage.daily.requests / usageData.limits.daily.requests) * 100, 100)}%` 
                                }}
                              />
                            </div>
                          )}
                          
                          <div className="flex justify-between text-xs">
                            <span>Tokens Used</span>
                            <span className="font-mono">
                              {Math.round(usageData.usage.daily.dataSizeBytes || 0).toLocaleString()}
                              {usageData.limits?.daily?.dataSize && usageData.limits.daily.dataSize !== -1 
                                ? `/${usageData.limits.daily.dataSize.toLocaleString()}` 
                                : '/∞'
                              }
                            </span>
                          </div>
                          
                          <div className="flex justify-between text-xs">
                            <span>Cost Today</span>
                            <span className="font-mono">${usageData.usage.daily.costUsd.toFixed(4)}</span>
                          </div>
                        </div>

                        {usageData.tier === 'free' && usageData.usage.daily.requests >= (usageData.limits?.daily?.requests || 0) * 0.8 && (
                          <Alert className="border-orange-200 bg-orange-50">
                            <AlertTriangle className="h-4 w-4 text-orange-600" />
                            <AlertDescription className="text-xs text-orange-800">
                              You're approaching your daily limit. 
                              <a href="/pricing" className="font-medium underline ml-1">Upgrade to Pro</a> for higher limits.
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    )}

                    {getCurrentModel() && (
                      <div className="p-3 bg-muted rounded-lg space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={getCategoryColor(getCurrentModel()!.category)}>
                            {getCategoryIcon(getCurrentModel()!.category)}
                            {getCurrentModel()!.category}
                          </Badge>
                          <div className="flex items-center gap-2">
                            {getModelIcon(getCurrentModel()!)}
                            <Badge variant="outline" className="flex items-center gap-1 text-xs">
                              {getCurrentModel()!.provider}
                            </Badge>
                          </div>
                          <Badge variant="outline" className="flex items-center gap-1 text-xs">
                            <DollarSign className="h-3 w-3" />
                            {getCurrentModel()!.pricing}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground wrap-break-word">
                          {getCurrentModel()!.description}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {getCurrentModel()!.features.slice(0, 3).map((feature, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {feature}
                            </Badge>
                          ))}
                          {getCurrentModel()!.features.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{getCurrentModel()!.features.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Model Availability Warning */}
                    {userAllowedModels && getCurrentModel() && !userAllowedModels.allowedModels.includes('*') && !userAllowedModels.allowedModels.includes(getCurrentModel()!.id) && (
                      <Alert className="border-orange-200 bg-orange-50">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        <AlertDescription className="text-orange-800">
                          The selected model "{getCurrentModel()!.name}" is not available in your {userAllowedModels.tier} plan.
                          Please select a different model or{' '}
                          <a href="/pricing" className="font-medium underline">
                            upgrade your plan
                          </a>
                          .
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-3">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Temperature: {temperature[0]}</label>
                        <Slider
                          value={temperature}
                          onValueChange={setTemperature}
                          max={1}
                          min={0}
                          step={0.1}
                          className="w-full"
                        />
                        <p className="text-xs text-muted-foreground">
                          Controls randomness. Lower = more focused, Higher = more creative
                        </p>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Max Tokens: {maxTokens[0]}</label>
                        <Slider
                          value={maxTokens}
                          onValueChange={setMaxTokens}
                          max={4000}
                          min={100}
                          step={100}
                          className="w-full"
                        />
                        <p className="text-xs text-muted-foreground">
                          Maximum length of the response
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2 shrink-0">
                      <Button 
                        onClick={clearChat} 
                        variant="outline" 
                        className="w-full hover-lift"
                        disabled={messages.length === 0}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Clear Chat
                      </Button>
                      <Button 
                        onClick={downloadChat} 
                        variant="outline" 
                        className="w-full hover-lift"
                        disabled={messages.length === 0}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download Chat
                      </Button>
                    </div>

                    <div className="p-3 bg-muted rounded-lg space-y-2 shrink-0">
                      <div className="flex items-center justify-between text-sm">
                        <span>Total Tokens:</span>
                        <span className="font-medium">{totalTokens.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>Total Cost:</span>
                        <span className="font-medium">${totalCost.toFixed(6)}</span>
                      </div>
                    </div>

                    {error && (
                      <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 shrink-0">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                        <AlertDescription className="text-red-800 dark:text-red-200">
                          {error}
                        </AlertDescription>
                      </Alert>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2">
            <Card className="card-enhanced h-[700px] flex flex-col min-h-0">
              <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageCircle className="h-5 w-5" />
                    <span>Chat</span>
                    {getCurrentModel() && (
                      <Badge variant="outline" className="ml-auto">
                        {getCurrentModel()!.name}
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    {messages.length} messages • {totalTokens.toLocaleString()} tokens • ${totalCost.toFixed(6)} cost
                  </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col space-y-4 min-h-0">
                <div className="flex-1 overflow-y-auto space-y-4 pr-2 min-h-0">
                  {messages.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Start a conversation with AI</p>
                      <p className="text-sm mt-2">Choose a model and start chatting!</p>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex gap-3 ${
                          message.role === 'user' ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div
                          className={`flex gap-2 max-w-[80%] ${
                            message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                          }`}
                        >
                          <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                            message.role === 'user' 
                              ? 'bg-blue-500 text-white' 
                              : 'bg-gray-500 text-white'
                          }`}>
                            {message.role === 'user' ? (
                              <User className="h-4 w-4" />
                            ) : (
                              <Bot className="h-4 w-4" />
                            )}
                          </div>
                          <div className={`rounded-lg p-3 ${
                            message.role === 'user'
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 dark:bg-gray-800'
                          }`}>
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm whitespace-pre-wrap wrap-break-word">{message.content}</p>
                              <Button
                                onClick={() => copyMessage(message.content)}
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 opacity-70 hover:opacity-100 shrink-0"
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                            <div className={`flex items-center gap-2 mt-2 text-xs flex-wrap ${
                              message.role === 'user' ? 'text-blue-100' : 'text-muted-foreground'
                            }`}>
                              <span>{message.timestamp.toLocaleTimeString()}</span>
                              {message.model && (
                                <Badge variant="outline" className="text-xs">
                                  {message.model}
                                </Badge>
                              )}
                              {message.tokensUsed && (
                                <span>• {message.tokensUsed} tokens</span>
                              )}
                              {message.cost && (
                                <span>• ${message.cost.toFixed(6)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  {isProcessing && (
                    <div className="flex gap-3 justify-start">
                      <div className="flex gap-2">
                        <div className="shrink-0 w-8 h-8 rounded-full bg-gray-500 text-white flex items-center justify-center">
                          <Bot className="h-4 w-4" />
                        </div>
                        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-sm">AI is thinking...</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="flex gap-2">
                  <Textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message here... (Press Enter to send)"
                    className="flex-1 resize-none min-h-0"
                    rows={3}
                    disabled={isProcessing}
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!inputMessage.trim() || isProcessing}
                    className="self-end hover-lift shrink-0"
                  >
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
